import { GitBranchPlus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Input, Modal, Textarea } from "@/components/common";
import { AgentSelect } from "@/components/imports/AgentSelect";
import { useAgentsForSelect } from "@/hooks/useAgents";
import type {
  ApprovalPolicyLine,
  SplitPolicyRequest,
} from "@/types/settlement";

function formatMoney(value: number): string {
  return (
    value.toLocaleString("pl-PL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " zł"
  );
}

interface SplitCommissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  policy: ApprovalPolicyLine | null;
  /** Agent in whose settlement we're operating (= primary unless `policy.split` says otherwise). */
  contextAgentId: string;
  contextAgentFullName: string;
  /** Tym razem 'primary' = agent prymarny tej polisy (nie zawsze == agent w kontekście rozliczenia). */
  onSubmit: (body: SplitPolicyRequest) => void;
  isLoading: boolean;
  errorMessage?: string | null;
}

/**
 * Modal podziału prowizji 50/50 (admin only).
 * - Wykluczamy z listy agenta prymarnego polisy.
 * - Pokazujemy live preview: kwota brutto każdej strony przed zastosowaniem indywidualnej stawki.
 * - Procent w zakresie 1–99 (granice wykluczone).
 */
export function SplitCommissionModal({
  isOpen,
  onClose,
  policy,
  contextAgentId,
  contextAgentFullName,
  onSubmit,
  isLoading,
  errorMessage,
}: SplitCommissionModalProps) {
  const { data: agents = [] } = useAgentsForSelect();

  // primary = agent z którego "wychodzi" % do co-agenta. Jeśli polisa już ma split,
  // primary to ten, na czyim koncie polisa technicznie istnieje (policy.split.isCurrentAgentPrimary
  // mówi czy `contextAgentId` jest prymarny).
  const isContextAgentPrimary = policy?.split?.isCurrentAgentPrimary ?? true;
  const primaryAgentId = isContextAgentPrimary
    ? contextAgentId
    : (policy?.split?.coAgentId ?? "");
  const primaryAgentName = isContextAgentPrimary
    ? contextAgentFullName
    : (agents.find((a) => a.id === primaryAgentId)?.fullName ??
      "agent prymarny");

  const isEditing = policy?.split !== null && policy?.split !== undefined;

  // Initial values
  const initialCoAgentId = policy?.split
    ? isContextAgentPrimary
      ? policy.split.coAgentId
      : contextAgentId
    : "";
  const initialPercentage = policy?.split
    ? Math.round(policy.split.coSharePercentage * 100)
    : 50;
  const initialNote = policy?.split?.note ?? "";

  const [coAgentId, setCoAgentId] = useState(initialCoAgentId);
  const [percentage, setPercentage] = useState<number>(initialPercentage);
  const [note, setNote] = useState(initialNote);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset whenever the policy changes
  useEffect(() => {
    if (!isOpen) return;
    setCoAgentId(initialCoAgentId);
    setPercentage(initialPercentage);
    setNote(initialNote);
    setValidationError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, policy?.policyId]);

  const coAgentName = useMemo(
    () => agents.find((a) => a.id === coAgentId)?.fullName ?? "wybrany agent",
    [agents, coAgentId],
  );

  if (!policy) return null;

  const commission = policy.commissionAmount;
  const coShare = (percentage / 100) * commission;
  const primaryShare = commission - coShare;

  function handleSubmit() {
    setValidationError(null);

    if (!coAgentId) {
      setValidationError("Wybierz agenta, z którym dzielisz prowizję.");
      return;
    }
    if (coAgentId === primaryAgentId) {
      setValidationError("Polisa nie może być dzielona z tym samym agentem.");
      return;
    }
    if (!Number.isFinite(percentage) || percentage <= 0 || percentage >= 100) {
      setValidationError("Procent musi być z zakresu 1–99.");
      return;
    }

    onSubmit({
      coAgentId,
      coSharePercentage: percentage / 100,
      note: note.trim() ? note.trim() : null,
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Edytuj podział prowizji" : "Podziel prowizję 50/50"}
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-edge-soft bg-subtle p-3">
          <div className="flex items-start gap-2">
            <GitBranchPlus
              size={16}
              className="mt-0.5 shrink-0 text-adara-600"
            />
            <div className="min-w-0 flex-1 text-xs text-sub">
              <p className="font-medium text-main">
                Polisa: {policy.policyNumber}
                {policy.clientName ? ` — ${policy.clientName}` : ""}
              </p>
              <p className="mt-0.5">
                Prowizja całkowita:{" "}
                <strong className="text-main">{formatMoney(commission)}</strong>
              </p>
              <p className="mt-0.5">
                Agent prymarny:{" "}
                <strong className="text-main">{primaryAgentName}</strong>
              </p>
            </div>
          </div>
        </div>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-main">
            Współagent
          </span>
          <AgentSelect
            value={coAgentId}
            onChange={setCoAgentId}
            placeholder="Wybierz agenta…"
            excludeIds={[primaryAgentId]}
          />
        </div>

        <div>
          <label
            className="mb-1.5 block text-sm font-medium text-main"
            htmlFor="split-percentage"
          >
            Udział współagenta (procent)
          </label>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <Input
              id="split-percentage"
              type="number"
              inputMode="numeric"
              min={1}
              max={99}
              step={1}
              value={percentage.toString()}
              onChange={(e) => {
                const n = Number.parseInt(e.target.value, 10);
                setPercentage(Number.isNaN(n) ? 0 : n);
              }}
              className="w-24"
            />
            <span className="text-sm text-sub">%</span>
            <span className="basis-full text-xs text-dim sm:ml-auto sm:basis-auto">
              {primaryAgentName.split(" ")[0]} dostanie {100 - percentage}%
            </span>
          </div>
        </div>

        <div>
          <label
            className="mb-1.5 block text-sm font-medium text-main"
            htmlFor="split-note"
          >
            Notatka (opcjonalnie)
          </label>
          <Textarea
            id="split-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Np. współpraca przy klientach VIP…"
            rows={2}
            maxLength={500}
          />
        </div>

        {/* Live preview — odporne na długie nazwiska: name truncate, kwoty stale po prawej. */}
        <div className="rounded-lg border border-edge-soft bg-panel p-3">
          <p className="mb-2 text-xs font-medium text-dim">
            Podgląd podziału
          </p>
          <dl className="space-y-1.5 text-sm">
            <div className="flex items-baseline gap-2">
              <dt className="min-w-0 flex-1 truncate text-sub">
                <span className="font-medium">{primaryAgentName}</span>{" "}
                <span className="text-xs text-dim">({100 - percentage}%)</span>
              </dt>
              <dd className="shrink-0 font-semibold tabular-nums text-main">
                {formatMoney(primaryShare)}
              </dd>
            </div>
            <div className="flex items-baseline gap-2">
              <dt className="min-w-0 flex-1 truncate text-sub">
                <span className="font-medium">{coAgentName}</span>{" "}
                <span className="text-xs text-dim">({percentage}%)</span>
              </dt>
              <dd className="shrink-0 font-semibold tabular-nums text-main">
                {formatMoney(coShare)}
              </dd>
            </div>
          </dl>
          <p className="mt-2 text-xs leading-snug text-dim">
            Powyższe kwoty to prowizja brutto z polisy. Końcowe wypłaty
            uwzględnią indywidualne stawki prowizyjne agentów.
          </p>
        </div>

        {validationError && (
          <Alert variant="warning" title="Sprawdź dane">
            {validationError}
          </Alert>
        )}
        {errorMessage && (
          <Alert variant="error" title="Nie udało się zapisać podziału">
            {errorMessage}
          </Alert>
        )}

        {/* Footer: na mobile pełna szerokość + primary action u góry (bliżej kciuka),
            na desktop klasyczne wyrównanie do prawej. */}
        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="sm:w-auto w-full"
          >
            Anuluj
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isLoading}
            className="sm:w-auto w-full"
          >
            {isEditing ? "Zapisz zmiany" : "Podziel prowizję"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
